/**
 * Design tokens for the marketing landing page.
 *
 * White page, ink `#0B0F19`, Inter everywhere — no serif, no mono, no aurora
 * gradients. Verdigris is NOT a marketing-level accent any more: it survives
 * only inside the product mock window and the hub-and-spoke diagram, which opt
 * in through the `.mkhero-brand` scope in `hero.css`.
 *
 * That scoping is why the accent slots read through `--mkh-accent*` custom
 * properties instead of literal colors: one and the same component renders ink
 * on the marketing page and verdigris inside the product window.
 *
 * Fonts come from CSS vars already loaded on <body> (see `app/layout.tsx`).
 */
const INTER_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

export const H = {
  // Core palette
  page: "#ffffff",
  ink: "#0B0F19",
  /** Ink on the marketing page, verdigris under `.mkhero-brand`. */
  accent: "var(--mkh-accent, #0B0F19)",
  accentSoft: "var(--mkh-accent-soft, rgba(11,15,25,0.06))",
  accentBorder: "var(--mkh-accent-border, rgba(11,15,25,0.24))",
  accentMid: "var(--mkh-accent-mid, rgba(11,15,25,0.5))",

  // Ink tints (used verbatim across the prototype)
  inkMute: "rgba(11,15,25,0.6)",
  inkFaint: "rgba(11,15,25,0.5)",
  inkSoft: "rgba(11,15,25,0.78)",
  inkDim: "rgba(11,15,25,0.45)",
  inkGhost: "rgba(11,15,25,0.34)",

  // Status colors (map §5)
  green: "#3A9E6B",
  amber: "#E0A23B",
  red: "#D8453E",
  blue: "#3F6CB0",
  purple: "#6D5BA6",
  /** Reserved ONLY for the "chaos" fake-app badges in the scroll stage. */
  orange: "#E8772E",

  /** Body / UI text. */
  sans: INTER_STACK,
  /** Marketing H1 / H2 — same family, set to 600 + tight tracking in `hero.css`. */
  display: INTER_STACK,
  /** Uppercase eyebrow + label text (letter-spaced at the call site). */
  label: INTER_STACK,
} as const;

/** Shared horizontal inset for the mobile HERO column (nav, chip carousel, …). */
export const MOBILE_HERO_GUTTER = 20;

/** Tighter inset for near-full-bleed bands (scroll stage, closing CTA). */
export const MOBILE_HERO_BAND_GUTTER = 10;

/** Drop shadow under the product window. */
export const WINDOW_SHADOW = "0 34px 64px -32px rgba(11,15,25,0.42)";

/** Flat light chrome behind the product window's translucent top bar. */
export const WINDOW_CHROME_BG = "#F4F4F5";

/** Hairline ring around the product window (replaces the old aurora plate). */
export const WINDOW_RING = "0 0 0 1px rgba(11,15,25,0.10)";

/** Closing CTA panel background — the one dark band on the page. */
export const DARK_PANEL_BG = "#0B0F19";

/** Shared module-interior geometry. */
export const MODULE_HEIGHT = 560;
export const SIDEBAR_WIDTH = 220;

/** Common border for spec/contact cards. */
export const CARD_BORDER = "#ECEEF1";
